import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  MapPin, 
  LogOut, 
  Users, 
  FileText, 
  ArrowLeft,
  Download,
  CalendarIcon,
  CheckCircle,
  XCircle,
  BarChart3,
  UserX,
  AlertTriangle,
  Euro,
  Upload,
  TrendingUp,
  Plus,
  Minus,
  Lock,
  FileDown,
  MapPin as MapPinIcon,
  Clock,
  Trash2,
  Eye,
  EyeOff,
  MessageCircle,
  Activity
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ExtraPaymentsSection from '../components/ExtraPaymentsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  setTimeout(() => {
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 200);
  }, 100);
};

const ReportsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [attendanceData, setAttendanceData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('monthly');
  const [forceCheckoutDialogOpen, setForceCheckoutDialogOpen] = useState(false);
  const [isForcing, setIsForcing] = useState(false);
  const [costData, setCostData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingBustaPaga, setEditingBustaPaga] = useState({});
  const [managingDays, setManagingDays] = useState(null); // {user_id, name, surname, presence_days_list}
  const [addDayDate, setAddDayDate] = useState([]);
  const [addDaysUserId, setAddDaysUserId] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(null);
  const [editingAcconti, setEditingAcconti] = useState({});
  const [accontiDialogUser, setAccontiDialogUser] = useState(null);
  const [newAccontoAmount, setNewAccontoAmount] = useState('');
  const [newAccontoNote, setNewAccontoNote] = useState('');
  const [detailsUser, setDetailsUser] = useState(null);
  const [annualData, setAnnualData] = useState(null);
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [expandedWorkers, setExpandedWorkers] = useState({});
  const [isLoadingAnnual, setIsLoadingAnnual] = useState(false);
  const [statsWorker, setStatsWorker] = useState('');
  const [statsData, setStatsData] = useState(null);
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [workersList, setWorkersList] = useState([]);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      let params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const [attendanceRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/attendance/all`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/reports/attendance-summary`, { params, withCredentials: true })
      ]);

      setAttendanceData(attendanceRes.data);
      setSummaryData(summaryRes.data);
    } catch (error) {
      // silenced
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  const fetchMonthlySummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/monthly-summary`, {
        params: { year: selectedYear, month: selectedMonth },
        withCredentials: true
      });
      setMonthlySummary(response.data);
    } catch (error) {
      // silenced
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  const fetchCostDashboard = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/cost-dashboard`, { withCredentials: true });
      setCostData(response.data);
    } catch (error) {
      // silenced
    }
  }, [isAdmin]);

  const fetchAnnualSummary = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoadingAnnual(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/annual-summary`, {
        params: { year: annualYear },
        withCredentials: true
      });
      setAnnualData(response.data);
    } catch (error) {
      // silenced
    } finally {
      setIsLoadingAnnual(false);
    }
  }, [isAdmin, annualYear]);

  const fetchWorkersList = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axios.get(`${API_URL}/api/workers`, { withCredentials: true });
      const workers = response.data.filter(w => w.role === 'worker');
      setWorkersList(workers);
      if (workers.length > 0 && !statsWorker) {
        setStatsWorker(workers[0].user_id);
      }
    } catch (error) {
      // silenced
    }
  }, [isAdmin, statsWorker]);

  const fetchWorkerStats = useCallback(async () => {
    if (!isAdmin || !statsWorker) return;
    setIsLoadingStats(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/worker-stats/${statsWorker}`, {
        params: { year: statsYear },
        withCredentials: true
      });
      setStatsData(response.data);
    } catch (error) {
      // silenced
    } finally {
      setIsLoadingStats(false);
    }
  }, [isAdmin, statsWorker, statsYear]);

  useEffect(() => {
    if (activeTab === 'daily') {
      fetchReports();
    } else if (activeTab === 'monthly') {
      fetchMonthlySummary();
    } else if (activeTab === 'annual') {
      fetchAnnualSummary();
    } else if (activeTab === 'stats') {
      fetchWorkersList();
    }
    if (activeTab === 'costs') {
      fetchCostDashboard();
    }
  }, [activeTab, fetchReports, fetchMonthlySummary, fetchCostDashboard, fetchAnnualSummary, fetchWorkersList]);

  useEffect(() => {
    if (activeTab === 'stats' && statsWorker) {
      fetchWorkerStats();
    }
  }, [activeTab, statsWorker, statsYear, fetchWorkerStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleForceCheckoutAll = async () => {
    setIsForcing(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/force-checkout-all`, {}, { withCredentials: true });
      alert(response.data.message);
      setForceCheckoutDialogOpen(false);
      fetchReports();
      fetchMonthlySummary();
    } catch (error) {
      // silenced
      alert('Errore durante il check-out forzato');
    } finally {
      setIsForcing(false);
    }
  };

  const handleUploadPayslip = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadResult(null);
    try {
      // Try multipart first, fallback to base64 for proxy limits
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('year', selectedYear);
        formData.append('month', selectedMonth);
        const response = await axios.post(`${API_URL}/api/admin/upload-payslips`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
        setUploadResult(response.data);
      } catch (multipartErr) {
        // Fallback: convert to base64 and send as JSON
        const reader = new FileReader();
        const b64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const response = await axios.post(`${API_URL}/api/admin/upload-payslips-b64`, {
          file_data: b64,
          filename: file.name,
          year: selectedYear,
          month: selectedMonth
        }, { withCredentials: true, timeout: 60000 });
        setUploadResult(response.data);
      }
      fetchMonthlySummary();
    } catch (error) {
      // silenced
      alert(error.response?.data?.detail || 'Errore nel caricamento del file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveBustaPaga = async (userId, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/monthly-payment/${userId}`, {
        year: selectedYear,
        month: selectedMonth,
        busta_paga: parseFloat(value) || 0
      }, { withCredentials: true });
      fetchMonthlySummary();
      setEditingBustaPaga(prev => { const n = {...prev}; delete n[userId]; return n; });
    } catch (error) {
      // silenced
    }
  };

  const handleAddDay = async (userId, dates) => {
    if (!dates || dates.length === 0) return;
    try {
      const dateStrs = dates.map(d => format(d, 'yyyy-MM-dd'));
      const res = await axios.post(`${API_URL}/api/admin/attendance-day`, {
        user_id: userId, dates: dateStrs
      }, { withCredentials: true });
      alert(res.data.message);
      fetchMonthlySummary();
      setAddDayDate([]);
      setAddDaysUserId(null);
    } catch (error) {
      // silenced
      alert('Errore nell\'aggiungere le giornate');
    }
  };

  const handleRemoveDay = async (userId, dateStr) => {
    if (!window.confirm(`Rimuovere la presenza del ${dateStr.split('-')[2]}/${dateStr.split('-')[1]}/${dateStr.split('-')[0]}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/attendance-day`, {
        data: { user_id: userId, date: dateStr },
        withCredentials: true
      });
      fetchMonthlySummary();
    } catch (error) {
      // silenced
    }
  };

  const handleExportWorkerPdf = async (worker) => {
    setExportingPdf(worker.user_id);
    try {
      const response = await axios.post(`${API_URL}/api/admin/export-worker-pdf`, {
        user_id: worker.user_id, year: selectedYear, month: selectedMonth
      }, { withCredentials: true, responseType: 'blob' });
      const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                          'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
      triggerDownload(
        new Blob([response.data], { type: 'application/pdf' }),
        `Riepilogo_${worker.name}_${worker.surname}_${monthNames[selectedMonth-1]}_${selectedYear}.pdf`
      );
    } catch (error) {
      // silenced
      alert('Errore nell\'esportazione PDF');
    } finally {
      setExportingPdf(null);
    }
  };

  const handleToggleBustaPagaPaid = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/payment-status/${userId}`, {
        year: selectedYear, month: selectedMonth,
        busta_paga_paid: !currentStatus
      }, { withCredentials: true });
      fetchMonthlySummary();
    } catch (error) {
      // silenced
    }
  };

  const handleAddAcconto = async (userId) => {
    const amount = parseFloat(newAccontoAmount);
    if (!amount || amount <= 0) { alert('Inserisci un importo valido'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/acconti/${userId}`, {
        year: selectedYear, month: selectedMonth,
        amount, note: newAccontoNote
      }, { withCredentials: true });
      setNewAccontoAmount('');
      setNewAccontoNote('');
      fetchMonthlySummary();
    } catch (error) {
      // silenced
    }
  };

  const handleDeleteAcconto = async (accontoId) => {
    if (!window.confirm('Rimuovere questo acconto?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/acconti/${accontoId}`, { withCredentials: true });
      fetchMonthlySummary();
    } catch (error) {
      // silenced
    }
  };

  const handleExportCSV = async () => {
    try {
      let params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await axios.get(`${API_URL}/api/reports/export-csv`, {
        params, withCredentials: true, responseType: 'blob'
      });
      triggerDownload(new Blob([response.data], { type: 'text/csv' }), `presenze_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    } catch (error) {
      // silenced
    }
  };

  const handleExportMonthlyCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/export-monthly-csv`, {
        params: { year: selectedYear, month: selectedMonth },
        withCredentials: true, responseType: 'blob'
      });
      triggerDownload(
        new Blob([response.data], { type: 'text/csv' }),
        `riepilogo_mensile_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`
      );
    } catch (error) {
      // silenced
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.setTextColor(0, 85, 255);
    doc.text('Report Presenze Operai', 14, 22);
    doc.setFontSize(10);
    doc.setTextColor(113, 113, 122);
    let dateText = 'Periodo: ';
    if (startDate && endDate) {
      dateText += `${format(startDate, 'dd/MM/yyyy', { locale: it })} - ${format(endDate, 'dd/MM/yyyy', { locale: it })}`;
    } else {
      dateText += 'Tutti i dati';
    }
    doc.text(dateText, 14, 30);

    const summaryTableData = summaryData.map(worker => [
      `${worker.name} ${worker.surname}`,
      worker.check_ins.toString(),
      worker.check_outs.toString()
    ]);

    autoTable(doc, {
      startY: 42,
      head: [['Nome', 'Entrate', 'Uscite']],
      body: summaryTableData,
      theme: 'grid',
      headStyles: { fillColor: [0, 85, 255] },
      styles: { fontSize: 10 }
    });

    const blob = doc.output('blob');
    triggerDownload(blob, `presenze_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  const handleExportMonthlyPDF = () => {
    if (!monthlySummary) return;
    const doc = new jsPDF('l');
    doc.setFontSize(20);
    doc.setTextColor(0, 85, 255);
    doc.text('Riepilogo Mensile Presenze', 14, 22);
    doc.setFontSize(12);
    doc.setTextColor(9, 9, 11);
    const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    doc.text(`${monthNames[selectedMonth - 1]} ${selectedYear}`, 14, 32);
    doc.setFontSize(10);
    doc.setTextColor(113, 113, 122);
    doc.text(`Giorni lavorativi nel mese: ${monthlySummary.working_days_in_month}`, 14, 40);

    if (isAdmin) {
      const head = [['Nome', 'Tariffa/GG', 'Giorni', 'Compenso', 'Busta Paga', 'Extra', 'Date Presenze']];
      const body = monthlySummary.summaries.map(s => [
        `${s.name} ${s.surname}`,
        `€${(s.daily_rate || 0).toFixed(2)}`,
        (s.presence_days_count || 0).toString(),
        `€${(s.compensation || 0).toFixed(2)}`,
        `€${(s.busta_paga || 0).toFixed(2)}`,
        `€${(s.extra || 0).toFixed(2)}`,
        (s.presence_days_list || []).map(d => d.split('-')[2]).join(', ')
      ]);
      body.push([
        'TOTALE', '', 
        monthlySummary.summaries.reduce((a, s) => a + (s.presence_days_count || 0), 0).toString(),
        `€${(monthlySummary.total_compensation || 0).toFixed(2)}`,
        `€${(monthlySummary.total_buste_paga || 0).toFixed(2)}`,
        `€${(monthlySummary.total_extra || 0).toFixed(2)}`,
        ''
      ]);
      autoTable(doc, { startY: 48, head, body, theme: 'grid', headStyles: { fillColor: [0, 85, 255] }, styles: { fontSize: 8 } });
    } else {
      const head = [['Nome', 'Giorni Presenza', 'Date Presenze']];
      const body = monthlySummary.summaries.map(s => [
        `${s.name} ${s.surname}`,
        (s.presence_days_count || 0).toString(),
        (s.presence_days_list || []).map(d => d.split('-')[2]).join(', ')
      ]);
      autoTable(doc, { startY: 48, head, body, theme: 'grid', headStyles: { fillColor: [0, 85, 255] }, styles: { fontSize: 9 } });
    }

    const blob = doc.output('blob');
    triggerDownload(blob, `riepilogo_mensile_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.pdf`);
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: it });
  };

  const totalCheckIns = summaryData.reduce((acc, w) => acc + w.check_ins, 0);
  const totalCheckOuts = summaryData.reduce((acc, w) => acc + w.check_outs, 0);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const monthsList = [
    { value: 1, label: 'Gennaio' }, { value: 2, label: 'Febbraio' },
    { value: 3, label: 'Marzo' }, { value: 4, label: 'Aprile' },
    { value: 5, label: 'Maggio' }, { value: 6, label: 'Giugno' },
    { value: 7, label: 'Luglio' }, { value: 8, label: 'Agosto' },
    { value: 9, label: 'Settembre' }, { value: 10, label: 'Ottobre' },
    { value: 11, label: 'Novembre' }, { value: 12, label: 'Dicembre' }
  ];

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E4E4E7] h-16 sm:h-20 flex items-center px-4 sm:px-8">
        <div className="flex items-center gap-3 flex-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mr-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-10 h-10 bg-[#0055FF] rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#09090B]">Report Presenze</h1>
          </div>
        </div>
        <nav className="flex items-center gap-2 sm:gap-4">
          {isAdmin && (
            <Button variant="outline" onClick={() => setForceCheckoutDialogOpen(true)} data-testid="force-checkout-btn"
              className="text-[#FF3B30] border-[#FF3B30] hover:bg-red-50 text-xs sm:text-sm">
              <UserX className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Chiudi Turni</span>
            </Button>
          )}
          <Button variant="ghost" onClick={() => navigate('/workers')} data-testid="nav-workers-btn" className="text-[#09090B] hover:bg-[#F4F4F5] hidden sm:flex">
            <Users className="w-4 h-4 mr-2" />Operai
          </Button>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="text-[#FF3B30] hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Esci</span>
          </Button>
        </nav>
      </header>

      <main className="p-4 sm:p-8 max-w-7xl mx-auto">
        {/* Tab Selector */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <Button variant={activeTab === 'monthly' ? 'default' : 'outline'} onClick={() => setActiveTab('monthly')}
            className={activeTab === 'monthly' ? 'bg-[#0055FF]' : ''} data-testid="tab-monthly">
            <CalendarIcon className="w-4 h-4 mr-2" />Riepilogo Mensile
          </Button>
          {isAdmin && (
            <Button variant={activeTab === 'costs' ? 'default' : 'outline'} onClick={() => setActiveTab('costs')}
              className={activeTab === 'costs' ? 'bg-[#0055FF]' : ''} data-testid="tab-costs">
              <TrendingUp className="w-4 h-4 mr-2" />Dashboard Costi
            </Button>
          )}
          {isAdmin && (
            <Button variant={activeTab === 'annual' ? 'default' : 'outline'} onClick={() => setActiveTab('annual')}
              className={activeTab === 'annual' ? 'bg-[#0055FF]' : ''} data-testid="tab-annual">
              <BarChart3 className="w-4 h-4 mr-2" />Riepilogo Annuale
            </Button>
          )}
          {isAdmin && (
            <Button variant={activeTab === 'stats' ? 'default' : 'outline'} onClick={() => setActiveTab('stats')}
              className={activeTab === 'stats' ? 'bg-[#0055FF]' : ''} data-testid="tab-stats">
              <Activity className="w-4 h-4 mr-2" />Statistiche
            </Button>
          )}
          <Button variant={activeTab === 'daily' ? 'default' : 'outline'} onClick={() => setActiveTab('daily')}
            className={activeTab === 'daily' ? 'bg-[#0055FF]' : ''} data-testid="tab-daily">
            <FileText className="w-4 h-4 mr-2" />Report Giornaliero
          </Button>
        </div>

        {/* ========== MONTHLY TAB ========== */}
        {activeTab === 'monthly' && (
          <>
            {/* Period Selection + Actions */}
            <Card className="border-[#E4E4E7] shadow-sm mb-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#0055FF]" />
                  Seleziona Periodo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Anno</label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-[120px]" data-testid="year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Mese</label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="w-[150px]" data-testid="month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthsList.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchMonthlySummary} variant="outline" className="border-[#E4E4E7]">Aggiorna</Button>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={handleExportMonthlyCSV} variant="outline" data-testid="export-monthly-csv-btn" className="border-[#E4E4E7]">
                      <Download className="w-4 h-4 mr-2" />CSV
                    </Button>
                    <Button onClick={handleExportMonthlyPDF} data-testid="export-monthly-pdf-btn" className="bg-[#0055FF] hover:bg-[#0040D0]">
                      <Download className="w-4 h-4 mr-2" />PDF
                    </Button>
                    {isAdmin && (
                      <>
                        <Button
                          onClick={async () => {
                            try {
                              const response = await axios.get(`${API_URL}/api/reports/export-monthly-pdf`, {
                                params: { year: selectedYear, month: selectedMonth },
                                responseType: 'blob',
                                withCredentials: true
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Report_${selectedMonth}_${selectedYear}.pdf`;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            } catch (e) { alert('Errore download report'); }
                          }}
                          data-testid="download-full-report-btn"
                          variant="outline"
                          className="border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/10"
                        >
                          <FileDown className="w-4 h-4 mr-2" />Report Completo
                        </Button>
                        <Button
                          onClick={() => {
                            const monthName = monthsList.find(m => m.value === selectedMonth)?.label || '';
                            const appUrl = window.location.origin;
                            const message = `Report presenze ${monthName} ${selectedYear}.\nAccedi per i dettagli: ${appUrl}/reports`;
                            window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                          }}
                          data-testid="share-report-whatsapp-btn"
                          variant="outline"
                          className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
                        >
                          <FaWhatsapp className="w-4 h-4 mr-2" />Condividi
                        </Button>
                      </>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2 items-end">
                      <input type="file" accept=".pdf" ref={fileInputRef} onChange={handleUploadPayslip} className="hidden" />
                      <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading}
                        data-testid="upload-payslip-btn" className="bg-[#34C759] hover:bg-[#2DB84D] text-white">
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploading ? 'Caricamento...' : 'Carica Buste Paga'}
                      </Button>
                    </div>
                  )}
                </div>
                {/* Upload result */}
                {uploadResult && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <p className="font-semibold text-green-800">{uploadResult.message}</p>
                    {uploadResult.matched?.length > 0 && (
                      <p className="text-green-700 mt-1">
                        Abbinati: {uploadResult.matched.map(m => `${m.name} ${m.surname} (€${m.netto_busta.toFixed(2)})`).join(', ')}
                      </p>
                    )}
                    {uploadResult.unmatched?.length > 0 && (
                      <p className="text-orange-600 mt-1">
                        Non trovati: {uploadResult.unmatched.map(u => `${u.pdf_name} (€${u.netto_busta.toFixed(2)})`).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Stats */}
            {monthlySummary && (
              <>
                <div className={`grid grid-cols-2 ${isAdmin ? 'sm:grid-cols-3 lg:grid-cols-6' : 'sm:grid-cols-3'} gap-3 mb-6`}>
                  <Card className="border-[#E4E4E7] shadow-sm">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-xs text-[#71717A]">Giorni Lavorativi</p>
                      <p className="text-2xl font-bold text-[#09090B]">{monthlySummary.working_days_in_month}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E4E4E7] shadow-sm">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-xs text-[#71717A]">Totale Operai</p>
                      <p className="text-2xl font-bold text-[#09090B]">{monthlySummary.total_workers}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E4E4E7] shadow-sm">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-xs text-[#71717A]">Tot. Giorni Presenza</p>
                      <p className="text-2xl font-bold text-[#34C759]">
                        {monthlySummary.summaries.reduce((acc, s) => acc + (s.presence_days_count || 0), 0)}
                      </p>
                    </CardContent>
                  </Card>
                  {isAdmin && (
                    <>
                      <Card className="border-[#E4E4E7] shadow-sm">
                        <CardContent className="pt-5 pb-4">
                          <p className="text-xs text-[#71717A]">Compenso Totale</p>
                          <p className="text-2xl font-bold text-[#0055FF]" data-testid="total-compensation">
                            €{(monthlySummary.total_compensation || 0).toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="border-[#E4E4E7] shadow-sm">
                        <CardContent className="pt-5 pb-4">
                          <p className="text-xs text-[#71717A]">Tot. Buste Paga</p>
                          <p className="text-2xl font-bold text-[#09090B]" data-testid="total-buste-paga">
                            €{(monthlySummary.total_buste_paga || 0).toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="border-[#E4E4E7] shadow-sm bg-[#FFCC00]/10">
                        <CardContent className="pt-5 pb-4">
                          <p className="text-xs text-[#71717A]">TOTALE EXTRA</p>
                          <p className="text-2xl font-bold text-[#09090B]" data-testid="total-extra">
                            €{(monthlySummary.total_extra || 0).toFixed(2)}
                          </p>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>

                {/* Monthly Summary Table */}
                <Card className="border-[#E4E4E7] shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-bold text-[#09090B] flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#0055FF]" />
                      Riepilogo - {monthsList.find(m => m.value === selectedMonth)?.label} {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="text-center py-8 text-[#71717A]">Caricamento...</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              {isAdmin && <TableHead className="text-center">€/GG</TableHead>}
                              <TableHead className="text-center">Giorni</TableHead>
                              {isAdmin && <TableHead className="text-center">Compenso</TableHead>}
                              {isAdmin && <TableHead className="text-center">Busta Paga</TableHead>}
                              {isAdmin && <TableHead className="text-center">Pagata</TableHead>}
                              {isAdmin && <TableHead className="text-center">Extra</TableHead>}
                              {isAdmin && <TableHead className="text-center">Acconti</TableHead>}
                              {isAdmin && <TableHead className="text-center">Pag. Extra</TableHead>}
                              {isAdmin && <TableHead className="text-center">Residuo</TableHead>}
                              <TableHead>Date</TableHead>
                              {isAdmin && <TableHead className="text-center">PDF</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {monthlySummary.summaries.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={isAdmin ? 11 : 3} className="text-center text-[#71717A] py-8">
                                  Nessun dato disponibile
                                </TableCell>
                              </TableRow>
                            ) : (
                              <>
                                {monthlySummary.summaries.map((s, index) => (
                                  <React.Fragment key={s.user_id}>
                                  <motion.tr initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.03 }}
                                    className={`hover:bg-[#F4F4F5] ${s.is_active === false ? 'opacity-50' : ''}`}>
                                    <TableCell className="font-medium">{s.name} {s.surname}</TableCell>
                                    {isAdmin && (
                                      <TableCell className="text-center text-[#71717A]">€{(s.daily_rate || 0).toFixed(2)}</TableCell>
                                    )}
                                    <TableCell className="text-center">
                                      <span className="text-[#34C759] font-semibold">{s.presence_days_count || 0}</span>
                                    </TableCell>
                                    {isAdmin && (
                                      <TableCell className="text-center font-bold text-[#0055FF]">€{(s.compensation || 0).toFixed(2)}</TableCell>
                                    )}
                                    {isAdmin && (
                                      <TableCell className="text-center">
                                        {editingBustaPaga[s.user_id] !== undefined ? (
                                          <div className="flex items-center gap-1 justify-center">
                                            <Input
                                              type="number" step="0.01" min="0"
                                              className="w-24 h-8 text-center text-sm"
                                              value={editingBustaPaga[s.user_id]}
                                              onChange={(e) => setEditingBustaPaga(prev => ({...prev, [s.user_id]: e.target.value}))}
                                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBustaPaga(s.user_id, editingBustaPaga[s.user_id]); }}
                                              autoFocus
                                              data-testid={`busta-paga-input-${s.user_id}`}
                                            />
                                            <Button size="sm" className="h-8 bg-[#34C759] hover:bg-[#2DB84D] px-2"
                                              onClick={() => handleSaveBustaPaga(s.user_id, editingBustaPaga[s.user_id])}>
                                              <CheckCircle className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => setEditingBustaPaga(prev => ({...prev, [s.user_id]: (s.busta_paga || 0).toFixed(2)}))}
                                            className="text-[#09090B] hover:text-[#0055FF] cursor-pointer underline-offset-2 hover:underline"
                                            data-testid={`busta-paga-edit-${s.user_id}`}
                                          >
                                            €{(s.busta_paga || 0).toFixed(2)}
                                          </button>
                                        )}
                                      </TableCell>
                                    )}
                                    {isAdmin && (
                                      <TableCell className="text-center">
                                        <button
                                          onClick={() => handleToggleBustaPagaPaid(s.user_id, s.busta_paga_paid)}
                                          data-testid={`toggle-paid-${s.user_id}`}
                                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold transition-colors ${
                                            s.busta_paga_paid 
                                              ? 'bg-[#34C759]/20 text-[#34C759]' 
                                              : 'bg-red-100 text-[#FF3B30]'
                                          }`}
                                        >
                                          {s.busta_paga_paid ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                          {s.busta_paga_paid ? 'Si' : 'No'}
                                        </button>
                                      </TableCell>
                                    )}
                                    {isAdmin && (
                                      <TableCell className={`text-center font-bold ${(s.extra || 0) > 0 ? 'text-[#FF9500]' : (s.extra || 0) < 0 ? 'text-[#FF3B30]' : 'text-[#71717A]'}`}>
                                        €{(s.extra || 0).toFixed(2)}
                                      </TableCell>
                                    )}
                                    {isAdmin && (
                                      <TableCell className="text-center">
                                        <button
                                          onClick={() => setAccontiDialogUser(s)}
                                          className="text-[#09090B] hover:text-[#0055FF] cursor-pointer underline-offset-2 hover:underline"
                                          data-testid={`acconti-edit-${s.user_id}`}
                                        >
                                          €{(s.acconti_extra || 0).toFixed(2)}
                                          {(s.acconti_list || []).length > 0 && (
                                            <span className="ml-1 text-[10px] text-[#71717A]">({(s.acconti_list || []).length})</span>
                                          )}
                                        </button>
                                      </TableCell>
                                    )}
                                    {isAdmin && (
                                      <TableCell className={`text-center font-bold ${(s.extra_payments_total || 0) > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
                                        €{(s.extra_payments_total || 0).toFixed(2)}
                                      </TableCell>
                                    )}
                                    {isAdmin && (
                                      <TableCell className={`text-center font-bold ${(s.extra_residuo || 0) > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
                                        €{(s.extra_residuo || 0).toFixed(2)}
                                      </TableCell>
                                    )}
                                    <TableCell>
                                      <div className="flex flex-wrap gap-1 max-w-[200px] items-center">
                                        {s.presence_days_list && s.presence_days_list.length > 0
                                          ? s.presence_days_list.map(d => (
                                            <span key={d} className="inline-flex items-center gap-0.5 text-xs text-[#34C759]">
                                              {d.split('-')[2]}
                                              {isAdmin && (
                                                <button onClick={() => handleRemoveDay(s.user_id, d)}
                                                  className="text-red-400 hover:text-red-600 ml-0.5" title="Rimuovi giorno">
                                                  <XCircle className="w-3 h-3" />
                                                </button>
                                              )}
                                            </span>
                                          ))
                                          : <span className="text-xs text-[#71717A]">-</span>}
                                        {isAdmin && (
                                          <>
                                            <button
                                              onClick={() => { setAddDaysUserId(s.user_id); setAddDayDate([]); }}
                                              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0055FF]/10 text-[#0055FF] hover:bg-[#0055FF]/20"
                                              title="Aggiungi giorni" data-testid={`add-day-${s.user_id}`}>
                                              <Plus className="w-3 h-3" />
                                            </button>
                                            {s.day_details && s.day_details.length > 0 && (
                                              <button
                                                onClick={() => setDetailsUser(s)}
                                                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-[#34C759] hover:bg-green-200"
                                                title="Vedi orari e posizioni" data-testid={`view-details-${s.user_id}`}>
                                                <Clock className="w-3 h-3" />
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </TableCell>
                                    {isAdmin && (
                                      <TableCell className="text-center">
                                        <div className="flex gap-1 justify-center">
                                          <Button size="sm" variant="outline"
                                            onClick={() => handleExportWorkerPdf(s)}
                                            disabled={exportingPdf === s.user_id}
                                            data-testid={`export-worker-pdf-${s.user_id}`}
                                            className="h-7 text-xs border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/10">
                                            <FileDown className="w-3 h-3 mr-1" />PDF
                                          </Button>
                                          <Button size="sm" variant="outline"
                                            onClick={() => {
                                              const worker = monthlySummary.summaries.find(w => w.user_id === s.user_id);
                                              if (!worker?.phone) {
                                                alert('Numero di telefono non configurato. Vai su Operai > Modifica per aggiungerlo.');
                                                return;
                                              }
                                              const url = `${API_URL}/api/admin/whatsapp-payslip-link/${s.user_id}?month=${selectedMonth}&year=${selectedYear}`;
                                              axios.get(url, { withCredentials: true }).then(res => {
                                                window.open(res.data.whatsapp_url, '_blank');
                                              }).catch(err => {
                                                alert(err.response?.data?.detail || 'Errore');
                                              });
                                            }}
                                            data-testid={`whatsapp-payslip-${s.user_id}`}
                                            className="h-7 text-xs border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10">
                                            <FaWhatsapp className="w-3 h-3 mr-1" />WA
                                          </Button>
                                        </div>
                                      </TableCell>
                                    )}
                                  </motion.tr>
                                  {isAdmin && (
                                    <tr key={`extra-${s.user_id}`}>
                                      <td colSpan={11} className="px-2 pb-2">
                                        <ExtraPaymentsSection userId={s.user_id} year={selectedYear} month={selectedMonth} dailyRate={s.daily_rate || 0} />
                                      </td>
                                    </tr>
                                  )}
                                  </React.Fragment>
                                ))}
                                {/* Total Row */}
                                {isAdmin && (
                                  <TableRow className="bg-[#F4F4F5] font-bold">
                                    <TableCell className="font-bold">TOTALE</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className="text-center font-bold">
                                      {monthlySummary.summaries.reduce((acc, s) => acc + (s.presence_days_count || 0), 0)}
                                    </TableCell>
                                    <TableCell className="text-center font-bold text-[#0055FF]">
                                      €{(monthlySummary.total_compensation || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-center font-bold">
                                      €{(monthlySummary.total_buste_paga || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className="text-center font-bold text-[#FF9500]">
                                      €{(monthlySummary.total_extra || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-center font-bold">
                                      €{monthlySummary.summaries.reduce((acc, s) => acc + (s.acconti_extra || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-center font-bold text-[#FF9500]">
                                      €{monthlySummary.summaries.reduce((acc, s) => acc + (s.extra_payments_total || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-center font-bold text-[#FF9500]">
                                      €{monthlySummary.summaries.reduce((acc, s) => acc + (s.extra_residuo || 0), 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* ========== COSTS DASHBOARD TAB ========== */}
        {activeTab === 'costs' && isAdmin && (
          <Card className="border-[#E4E4E7] shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#0055FF]" />
                Andamento Costi Mensili (Ultimi 12 mesi)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {costData.length === 0 ? (
                <div className="text-center py-8 text-[#71717A]">Nessun dato disponibile</div>
              ) : (
                <>
                  <div className="h-[400px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={costData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                        <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                        <Legend />
                        <Bar dataKey="compenso" name="Compenso" fill="#0055FF" radius={[4,4,0,0]} />
                        <Bar dataKey="busta_paga" name="Busta Paga" fill="#34C759" radius={[4,4,0,0]} />
                        <Bar dataKey="extra" name="Extra" fill="#FF9500" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Summary table */}
                  <div className="overflow-x-auto mt-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mese</TableHead>
                          <TableHead className="text-right">Compenso</TableHead>
                          <TableHead className="text-right">Busta Paga</TableHead>
                          <TableHead className="text-right">Extra</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costData.map((row) => (
                          <TableRow key={`${row.year}-${row.month}`} className="hover:bg-[#F4F4F5]">
                            <TableCell className="font-medium">{row.label}</TableCell>
                            <TableCell className="text-right text-[#0055FF] font-semibold">€{row.compenso.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-[#34C759] font-semibold">€{row.busta_paga.toFixed(2)}</TableCell>
                            <TableCell className={`text-right font-semibold ${row.extra > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
                              €{row.extra.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ========== STATISTICS TAB ========== */}
        {activeTab === 'stats' && isAdmin && (
          <>
            <Card className="border-[#E4E4E7] shadow-sm mb-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#0055FF]" />
                  Statistiche Presenze
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-end flex-wrap">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Operaio</label>
                    <Select value={statsWorker} onValueChange={setStatsWorker}>
                      <SelectTrigger className="w-[220px]" data-testid="stats-worker-select"><SelectValue placeholder="Seleziona operaio" /></SelectTrigger>
                      <SelectContent>
                        {workersList.map(w => (
                          <SelectItem key={w.user_id} value={w.user_id}>{w.name} {w.surname}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Anno</label>
                    <Select value={statsYear.toString()} onValueChange={(v) => setStatsYear(parseInt(v))}>
                      <SelectTrigger className="w-[120px]" data-testid="stats-year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {isLoadingStats ? (
              <div className="text-center py-12 text-[#71717A]">Caricamento statistiche...</div>
            ) : statsData ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Card className="border-[#E4E4E7] shadow-sm">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-[#0055FF]">{statsData.total_days_worked}</p>
                      <p className="text-sm text-[#71717A]">Giorni Lavorati</p>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E4E4E7] shadow-sm">
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-[#71717A]">{statsData.total_working_days}</p>
                      <p className="text-sm text-[#71717A]">Giorni Lavorativi</p>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E4E4E7] shadow-sm">
                    <CardContent className="pt-6 text-center">
                      <p className={`text-3xl font-bold ${statsData.attendance_percentage >= 80 ? 'text-[#34C759]' : statsData.attendance_percentage >= 50 ? 'text-[#FF9500]' : 'text-[#FF3B30]'}`}>
                        {statsData.attendance_percentage}%
                      </p>
                      <p className="text-sm text-[#71717A]">Percentuale Presenza</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Weekly Chart */}
                <Card className="border-[#E4E4E7] shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-[#09090B]">Ultimi 7 Giorni</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2 justify-center">
                      {statsData.weekly.map((d) => (
                        <div key={d.date} className="flex flex-col items-center gap-1">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${
                            d.present ? 'bg-[#34C759] text-white' : 'bg-[#F4F4F5] text-[#71717A]'
                          }`}>
                            {d.present ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                          </div>
                          <span className="text-xs font-semibold text-[#09090B]">{d.day_name}</span>
                          <span className="text-[10px] text-[#71717A]">{d.date.slice(5)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly Chart */}
                <Card className="border-[#E4E4E7] shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold text-[#09090B]">Presenze Mensili — {statsYear}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={statsData.monthly.map(m => ({
                        ...m,
                        mese: monthsList.find(ml => ml.value === m.month)?.label?.slice(0, 3)
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                        <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(value) => [`${value} giorni`, 'Presenze']}
                          labelFormatter={(label) => monthsList.find(ml => ml.label?.startsWith(label))?.label || label}
                        />
                        <Bar dataKey="days_worked" fill="#0055FF" radius={[4, 4, 0, 0]} name="Giorni Lavorati" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12 text-[#71717A]">Seleziona un operaio per visualizzare le statistiche</div>
            )}
          </>
        )}

        {/* ========== ANNUAL SUMMARY TAB ========== */}
        {activeTab === 'annual' && isAdmin && (
          <>
            <Card className="border-[#E4E4E7] shadow-sm mb-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#0055FF]" />
                  Riepilogo Annuale per Operaio
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-end">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Anno</label>
                    <Select value={annualYear.toString()} onValueChange={(v) => setAnnualYear(parseInt(v))}>
                      <SelectTrigger className="w-[120px]" data-testid="annual-year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchAnnualSummary} variant="outline" className="border-[#E4E4E7]">Aggiorna</Button>
                  <Button
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API_URL}/api/admin/export-annual-pdf`, {
                          params: { year: annualYear },
                          responseType: 'blob',
                          withCredentials: true
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Riepilogo_Annuale_Tutti_${annualYear}.pdf`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch (e) { alert('Errore download PDF'); }
                    }}
                    data-testid="export-annual-all-pdf-btn"
                    className="bg-[#0055FF] hover:bg-[#0040D0]"
                  >
                    <Download className="w-4 h-4 mr-2" />PDF Tutti
                  </Button>
                </div>
              </CardContent>
            </Card>

            {isLoadingAnnual ? (
              <div className="text-center py-12 text-[#71717A]">Caricamento dati annuali...</div>
            ) : annualData && annualData.workers?.length > 0 ? (
              <div className="space-y-4">
                {annualData.workers.map((worker) => {
                  const isExpanded = expandedWorkers[worker.user_id] !== false;
                  return (
                    <Card key={worker.user_id} className={`border-[#E4E4E7] shadow-sm ${!worker.is_active ? 'opacity-60' : ''}`}>
                      <button
                        onClick={() => setExpandedWorkers(prev => ({ ...prev, [worker.user_id]: !isExpanded }))}
                        className="w-full text-left"
                        data-testid={`annual-worker-toggle-${worker.user_id}`}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#0055FF]/10 rounded-full flex items-center justify-center">
                                <span className="text-sm font-bold text-[#0055FF]">
                                  {worker.name[0]}{worker.surname[0]}
                                </span>
                              </div>
                              <div>
                                <CardTitle className="text-base font-bold text-[#09090B]">
                                  {worker.name} {worker.surname}
                                </CardTitle>
                                <p className="text-xs text-[#71717A]">
                                  Tariffa: €{worker.daily_rate.toFixed(2)}/gg | Giorni totali: {worker.totals.days_worked} | Anno {annualYear}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right hidden sm:block">
                                <p className="text-xs text-[#71717A]">Residuo annuale</p>
                                <p className={`text-lg font-bold ${worker.totals.residuo > 0 ? 'text-[#FF9500]' : worker.totals.residuo < 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                                  €{worker.totals.residuo.toFixed(2)}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  axios.get(`${API_URL}/api/admin/export-annual-pdf`, {
                                    params: { year: annualYear, user_id: worker.user_id },
                                    responseType: 'blob',
                                    withCredentials: true
                                  }).then(res => {
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `Riepilogo_${worker.name}_${worker.surname}_${annualYear}.pdf`;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                  }).catch(() => alert('Errore download PDF'));
                                }}
                                data-testid={`export-annual-worker-pdf-${worker.user_id}`}
                                className="h-7 text-xs border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/10"
                              >
                                <FileDown className="w-3 h-3 mr-1" />PDF
                              </Button>
                              <svg className={`w-5 h-5 text-[#71717A] transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </CardHeader>
                      </button>
                      {isExpanded && (
                        <CardContent className="pt-0">
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-[#F4F4F5]">
                                  <TableHead className="font-bold">Mese</TableHead>
                                  <TableHead className="text-center font-bold">Giorni</TableHead>
                                  <TableHead className="text-center font-bold">Compenso</TableHead>
                                  <TableHead className="text-center font-bold">Busta Paga</TableHead>
                                  <TableHead className="text-center font-bold">Pagata</TableHead>
                                  <TableHead className="text-center font-bold">Extra</TableHead>
                                  <TableHead className="text-center font-bold">Acconti</TableHead>
                                  <TableHead className="text-center font-bold">Pag. Extra</TableHead>
                                  <TableHead className="text-center font-bold">Da Dare</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {worker.months.map((m) => (
                                  <TableRow key={m.month} className={`hover:bg-[#F4F4F5]/50 ${m.days_worked === 0 ? 'opacity-40' : ''}`}>
                                    <TableCell className="font-medium">{monthsList.find(ml => ml.value === m.month)?.label}</TableCell>
                                    <TableCell className="text-center">
                                      <span className={`font-semibold ${m.days_worked > 0 ? 'text-[#34C759]' : 'text-[#71717A]'}`}>{m.days_worked}</span>
                                    </TableCell>
                                    <TableCell className="text-center font-semibold text-[#0055FF]">€{m.compensation.toFixed(2)}</TableCell>
                                    <TableCell className="text-center">€{m.busta_paga.toFixed(2)}</TableCell>
                                    <TableCell className="text-center">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        m.busta_paga_paid ? 'bg-[#34C759]/20 text-[#34C759]' : 'bg-red-100 text-[#FF3B30]'
                                      }`}>
                                        {m.busta_paga_paid ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                        {m.busta_paga_paid ? 'Si' : 'No'}
                                      </span>
                                    </TableCell>
                                    <TableCell className={`text-center font-semibold ${m.extra > 0 ? 'text-[#FF9500]' : m.extra < 0 ? 'text-[#FF3B30]' : 'text-[#71717A]'}`}>
                                      €{m.extra.toFixed(2)}
                                    </TableCell>
                                    <TableCell className="text-center">€{m.acconti.toFixed(2)}</TableCell>
                                    <TableCell className={`text-center font-semibold ${(m.extra_payments_total || 0) > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
                                      €{(m.extra_payments_total || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell className={`text-center font-bold ${m.residuo > 0 ? 'text-[#FF9500]' : m.residuo < 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                                      €{m.residuo.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {/* Annual Total Row */}
                                <TableRow className="bg-[#0055FF]/5 border-t-2 border-[#0055FF]">
                                  <TableCell className="font-bold text-[#0055FF]">TOTALE ANNUALE</TableCell>
                                  <TableCell className="text-center font-bold">{worker.totals.days_worked}</TableCell>
                                  <TableCell className="text-center font-bold text-[#0055FF]">€{worker.totals.compensation.toFixed(2)}</TableCell>
                                  <TableCell className="text-center font-bold">€{worker.totals.busta_paga.toFixed(2)}</TableCell>
                                  <TableCell></TableCell>
                                  <TableCell className={`text-center font-bold ${worker.totals.extra > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
                                    €{worker.totals.extra.toFixed(2)}
                                  </TableCell>
                                  <TableCell className="text-center font-bold">€{worker.totals.acconti.toFixed(2)}</TableCell>
                                  <TableCell className={`text-center font-bold ${(worker.totals.extra_payments_total || 0) > 0 ? 'text-[#FF9500]' : 'text-[#71717A]'}`}>
                                    €{(worker.totals.extra_payments_total || 0).toFixed(2)}
                                  </TableCell>
                                  <TableCell className={`text-center font-bold text-lg ${worker.totals.residuo > 0 ? 'text-[#FF9500]' : worker.totals.residuo < 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                                    €{worker.totals.residuo.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-[#71717A]">Nessun dato disponibile per {annualYear}</div>
            )}
          </>
        )}

        {/* ========== DAILY TAB ========== */}
        {activeTab === 'daily' && (
          <>
            <Card className="border-[#E4E4E7] shadow-sm mb-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0055FF]" />
                  Filtri e Esportazione
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Data Inizio</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="start-date-btn" className="w-[180px] justify-start text-left font-normal border-[#E4E4E7]">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, 'dd/MM/yyyy', { locale: it }) : 'Seleziona'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#09090B]">Data Fine</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="end-date-btn" className="w-[180px] justify-start text-left font-normal border-[#E4E4E7]">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, 'dd/MM/yyyy', { locale: it }) : 'Seleziona'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleExportCSV} variant="outline" data-testid="export-csv-btn" className="border-[#E4E4E7]">
                      <Download className="w-4 h-4 mr-2" />CSV
                    </Button>
                    <Button onClick={handleExportPDF} data-testid="export-pdf-btn" className="bg-[#0055FF] hover:bg-[#0040D0]">
                      <Download className="w-4 h-4 mr-2" />PDF
                    </Button>
                  </div>
                  {(startDate || endDate) && (
                    <Button variant="ghost" onClick={() => { setStartDate(null); setEndDate(null); }} className="text-[#71717A]">Pulisci</Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <Card className="border-[#E4E4E7] shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-[#34C759]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#71717A]">Totale Entrate</p>
                      <p className="text-2xl font-bold text-[#09090B]">{totalCheckIns}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-[#E4E4E7] shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-[#FF3B30]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#71717A]">Totale Uscite</p>
                      <p className="text-2xl font-bold text-[#09090B]">{totalCheckOuts}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-[#E4E4E7] shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-[#0055FF]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#71717A]">Operai Attivi</p>
                      <p className="text-2xl font-bold text-[#09090B]">{summaryData.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detail Table */}
            <Card className="border-[#E4E4E7] shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0055FF]" />Dettaglio Presenze
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-center py-8 text-[#71717A]">Caricamento...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Azione</TableHead>
                          <TableHead>Data/Ora</TableHead>
                          <TableHead>Posizione</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attendanceData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-[#71717A] py-8">Nessuna presenza registrata</TableCell>
                          </TableRow>
                        ) : (
                          attendanceData.slice(0, 50).map((record) => (
                            <TableRow key={record.id} className="hover:bg-[#F4F4F5]">
                              <TableCell className="font-medium">{record.user_name} {record.user_surname}</TableCell>
                              <TableCell>
                                {record.action === 'check_in' ? (
                                  <span className="inline-flex items-center gap-1 text-[#34C759]"><CheckCircle className="w-4 h-4" />Entrata</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[#FF3B30]"><XCircle className="w-4 h-4" />Uscita</span>
                                )}
                              </TableCell>
                              <TableCell>{formatDate(record.timestamp)}</TableCell>
                              <TableCell className="text-sm text-[#71717A]">{record.latitude.toFixed(4)}, {record.longitude.toFixed(4)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Acconti Dialog */}
      <Dialog open={accontiDialogUser !== null} onOpenChange={(open) => { if (!open) setAccontiDialogUser(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0055FF]">
              <Euro className="w-5 h-5" />
              Acconti Extra — {accontiDialogUser?.name} {accontiDialogUser?.surname}
            </DialogTitle>
            <DialogDescription>
              Extra totale: €{(accontiDialogUser?.extra || 0).toFixed(2)} | Acconti: €{(accontiDialogUser?.acconti_extra || 0).toFixed(2)} | Pag. Extra: €{(accontiDialogUser?.extra_payments_total || 0).toFixed(2)} | Residuo: €{(accontiDialogUser?.extra_residuo || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          {/* List existing acconti */}
          <div className="max-h-[200px] overflow-y-auto space-y-2">
            {(accontiDialogUser?.acconti_list || []).length === 0 ? (
              <p className="text-sm text-[#71717A] text-center py-4">Nessun acconto registrato</p>
            ) : (
              (accontiDialogUser?.acconti_list || []).map((a) => (
                <div key={a.acconto_id} className="flex items-center justify-between p-2 bg-[#F4F4F5] rounded-lg">
                  <div>
                    <span className="font-semibold text-[#09090B]">€{a.amount.toFixed(2)}</span>
                    {a.note && <span className="text-sm text-[#71717A] ml-2">{a.note}</span>}
                    <p className="text-xs text-[#71717A]">{a.created_at ? new Date(a.created_at).toLocaleDateString('it-IT') : ''}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteAcconto(a.acconto_id)}
                    className="text-[#FF3B30] hover:bg-red-50 h-8 w-8 p-0" data-testid={`delete-acconto-${a.acconto_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
          {/* Add new acconto */}
          <div className="flex gap-2 items-end pt-2 border-t border-[#E4E4E7]">
            <div className="flex-1 space-y-1">
              <label className="text-xs font-semibold text-[#09090B]">Importo (€)</label>
              <Input type="number" step="0.01" min="0" placeholder="0.00" value={newAccontoAmount}
                onChange={(e) => setNewAccontoAmount(e.target.value)}
                data-testid="new-acconto-amount" className="h-9" />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-xs font-semibold text-[#09090B]">Nota</label>
              <Input placeholder="es: Anticipo 10/03" value={newAccontoNote}
                onChange={(e) => setNewAccontoNote(e.target.value)}
                data-testid="new-acconto-note" className="h-9" />
            </div>
            <Button onClick={() => handleAddAcconto(accontiDialogUser?.user_id)}
              disabled={!newAccontoAmount} data-testid="add-acconto-btn"
              className="bg-[#34C759] hover:bg-[#2DB84D] h-9">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day Details Dialog (Orari + Posizione) */}
      <Dialog open={detailsUser !== null} onOpenChange={(open) => { if (!open) setDetailsUser(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0055FF]">
              <Clock className="w-5 h-5" />
              Dettagli Presenze — {detailsUser?.name} {detailsUser?.surname}
            </DialogTitle>
            <DialogDescription>
              Orari di entrata/uscita e posizione GPS per ogni giornata
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[350px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-center">Entrata</TableHead>
                  <TableHead className="text-center">Uscita</TableHead>
                  <TableHead className="text-center">Posizione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(detailsUser?.day_details || []).map((dd) => (
                  <TableRow key={dd.date}>
                    <TableCell className="font-medium">{dd.date.split('-')[2]}/{dd.date.split('-')[1]}</TableCell>
                    <TableCell className="text-center text-[#34C759] font-semibold">{dd.check_in}</TableCell>
                    <TableCell className="text-center text-[#FF3B30] font-semibold">{dd.check_out}</TableCell>
                    <TableCell className="text-center">
                      {dd.lat !== 0 || dd.lng !== 0 ? (
                        <a href={`https://maps.google.com/?q=${dd.lat},${dd.lng}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#0055FF] hover:underline">
                          <MapPinIcon className="w-3 h-3" />{dd.lat.toFixed(4)}
                        </a>
                      ) : (
                        <span className="text-xs text-[#71717A]">Manuale</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-Date Selection Dialog */}
      <Dialog open={addDaysUserId !== null} onOpenChange={(open) => { if (!open) { setAddDaysUserId(null); setAddDayDate([]); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0055FF]">
              <CalendarIcon className="w-5 h-5" />
              Aggiungi Giorni di Presenza
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const worker = monthlySummary?.summaries?.find(s => s.user_id === addDaysUserId);
                return worker ? `${worker.name} ${worker.surname} — seleziona le date` : 'Seleziona le date';
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            <Calendar
              mode="multiple"
              selected={addDayDate}
              onSelect={(dates) => setAddDayDate(dates || [])}
              defaultMonth={new Date(selectedYear, selectedMonth - 1)}
              data-testid="multi-date-calendar"
            />
          </div>
          {addDayDate.length > 0 && (
            <p className="text-sm text-center text-[#71717A]">
              {addDayDate.length} {addDayDate.length === 1 ? 'giorno selezionato' : 'giorni selezionati'}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddDaysUserId(null); setAddDayDate([]); }}>Annulla</Button>
            <Button
              onClick={() => handleAddDay(addDaysUserId, addDayDate)}
              disabled={addDayDate.length === 0}
              data-testid="confirm-add-days-btn"
              className="bg-[#0055FF] hover:bg-[#0040D0]">
              Aggiungi {addDayDate.length > 0 ? `${addDayDate.length} giorni` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Checkout Dialog */}
      <Dialog open={forceCheckoutDialogOpen} onOpenChange={setForceCheckoutDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#FF3B30]">
              <AlertTriangle className="w-5 h-5" />Chiudi Tutti i Turni
            </DialogTitle>
            <DialogDescription>
              Questa azione forzerà il check-out per tutti gli operai attualmente in servizio. Sei sicuro di voler procedere?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceCheckoutDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleForceCheckoutAll} disabled={isForcing} data-testid="confirm-force-checkout-btn"
              className="bg-[#FF3B30] hover:bg-[#E63329] text-white">
              {isForcing ? 'Chiusura in corso...' : 'Conferma Chiusura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
